/**
 * The option controls shared by Convert Image, Bulk Image and WebP Converter. Each piece takes the form and an
 * onChange(patch); the page decides what a change means for its own result (usually: forget it).
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { NoticeBox } from "@/components/NoticeBox";
import { canEncodeType, AVIF_UNSUPPORTED_REASON, WEBP_UNSUPPORTED_REASON, clampSliderQuality } from "./imageFormats";
import { FORMATS, formatInfo, rgbaToHex, ICO_SIZES, FIT_MODES, type OutFormat, type FitMode } from "./convertCore";
import { FIT_HELP, needsFill, type ConvertForm } from "./convertForm";

export type FormPatch = (patch: Partial<ConvertForm>) => void;

/** Whether this browser can write WebP and AVIF (null while still being checked). */
export function useEncodeSupport(): Record<string, boolean | null> {
  const [can, setCan] = useState<Record<string, boolean | null>>({ "image/webp": null, "image/avif": null });
  useEffect(() => {
    for (const t of ["image/webp", "image/avif"]) canEncodeType(t).then((ok) => setCan((s) => ({ ...s, [t]: ok })));
  }, []);
  return can;
}

export const formatUnavailable = (can: Record<string, boolean | null>, id: OutFormat) =>
  (id === "webp" && can["image/webp"] === false) || (id === "avif" && can["image/avif"] === false);

function parseColorSafe(hex: string) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  const n = m ? parseInt(m[1], 16) : 0xffffff;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
}

export function Check({ id, checked, onChange, label, help }: { id: string; checked: boolean; onChange: (v: boolean) => void; label: string; help?: string }) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <div>
        <Label htmlFor={id} className="text-sm font-normal cursor-pointer">{label}</Label>
        {help && <p className="text-xs text-muted-foreground">{help}</p>}
      </div>
    </div>
  );
}

export function ColorPicker({ id, form, onChange }: { id: string; form: Pick<ConvertForm, "bgColor">; onChange: (patch: { bgColor: string }) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="text-sm">Fill color:</Label>
      <input id={id} type="color" value={rgbaToHex(parseColorSafe(form.bgColor))} onChange={(e) => onChange({ bgColor: e.target.value })} className="h-8 w-14 rounded border cursor-pointer" />
    </div>
  );
}

export function FormatPicker({ form, onChange, support, formats }: { form: ConvertForm; onChange: FormPatch; support: Record<string, boolean | null>; formats?: readonly OutFormat[] }) {
  const info = formatInfo(form.format);
  const list = formats ? FORMATS.filter((f) => formats.includes(f.id)) : FORMATS;
  return (
    <div className="space-y-1">
      <Label>Convert to</Label>
      <Select value={form.format} onValueChange={(v) => onChange({ format: v as OutFormat })}>
        <SelectTrigger className="w-48" aria-label="Convert to format"><SelectValue /></SelectTrigger>
        <SelectContent>{list.map((f) => <SelectItem key={f.id} value={f.id} disabled={formatUnavailable(support, f.id)}>{f.label}{formatUnavailable(support, f.id) ? " (not available here)" : ""}</SelectItem>)}</SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{info.hint}</p>
      {support["image/avif"] === false && form.format !== "avif" && <p className="text-xs text-muted-foreground">{AVIF_UNSUPPORTED_REASON}</p>}
      {support["image/webp"] === false && form.format !== "webp" && <p className="text-xs text-muted-foreground">{WEBP_UNSUPPORTED_REASON}</p>}
    </div>
  );
}

export function QualityControl({ form, onChange, sourceEstimate, min = 40 }: { form: ConvertForm; onChange: FormPatch; sourceEstimate?: number | null; min?: number }) {
  if (!formatInfo(form.format).lossy) return null;
  return (
    <div className="space-y-2 max-w-sm">
      <Label>Quality: {form.quality}%</Label>
      <Slider value={[form.quality]} onValueChange={(v) => onChange({ quality: v[0] })} min={min} max={100} step={1} />
      {sourceEstimate != null && (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange({ quality: clampSliderQuality(sourceEstimate) })}>
          Same as source (estimate: about {sourceEstimate}%)
        </Button>
      )}
      <p className="text-xs text-muted-foreground">Lower quality makes a smaller file. 90 and above looks the same to most eyes.</p>
    </div>
  );
}

/** ICO sizes and BMP depth: the options that only exist for one format each. */
export function FormatSpecificControls({ form, onChange }: { form: ConvertForm; onChange: FormPatch }) {
  return (
    <>
      {form.format === "ico" && (
        <div className="space-y-2">
          <Label>Icon sizes (pixels)</Label>
          <div className="flex flex-wrap gap-4">
            {ICO_SIZES.map((s) => (
              <div key={s} className="flex items-center gap-2">
                <Checkbox id={`ico-${s}`} checked={form.icoSizes.includes(s)} onCheckedChange={(v) => onChange({ icoSizes: v === true ? [...form.icoSizes, s].sort((a, b) => a - b) : form.icoSizes.filter((n) => n !== s) })} />
                <Label htmlFor={`ico-${s}`} className="text-sm font-normal cursor-pointer">{s}</Label>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">One icon file holds all ticked sizes. 16, 32 and 48 cover browser tabs and shortcuts; add 256 for large Windows icons. A picture that is not square is centred with see-through padding.</p>
        </div>
      )}
      {form.format === "bmp" && (
        <div className="space-y-1">
          <Label>Colour depth</Label>
          <Select value={form.bmpDepth} onValueChange={(v) => onChange({ bmpDepth: v })}>
            <SelectTrigger className="w-64" aria-label="BMP colour depth"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="24">24-bit (no transparency)</SelectItem>
              <SelectItem value="32">32-bit (keeps transparency)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {form.format === "gif" && <p className="text-xs text-muted-foreground">GIF holds 256 colours at most, so photos look flatter. Only the first frame of an animated picture is used here.</p>}
    </>
  );
}

/** Shown when the chosen format cannot be see-through and the source might be. */
export function FillNotice({ form, onChange, show, many = false }: { form: ConvertForm; onChange: FormPatch; show: boolean; many?: boolean }) {
  if (!needsFill(form) || !show) return null;
  const label = formatInfo(form.format).label;
  return (
    <NoticeBox className="space-y-2" testId="alpha-notice">
      <p className="text-sm">
        {label} doesn't support transparency, so the transparent areas of {many ? "the images" : "this image"} will be filled with the
        color below instead. This can't be undone from the {label} afterward.
      </p>
      <ColorPicker id="bg-color" form={form} onChange={onChange} />
    </NoticeBox>
  );
}

export function optionError(form: ConvertForm, parsedError: string | null): string | null {
  if (form.format === "ico" && form.icoSizes.length === 0) return "Tick at least one icon size.";
  return parsedError;
}

/** The collapsible "More options" box: size, background, turn/flip, colour, details. `sizeExtra` adds controls to the Size group. */
export function MoreOptions({ form, onChange, metaHelp, sizeExtra, open = false }: { form: ConvertForm; onChange: FormPatch; metaHelp: string; sizeExtra?: ReactNode; open?: boolean }) {
  const info = formatInfo(form.format);
  const showOptionalFill = !needsFill(form) && (info.alpha || form.fit === "contain");
  return (
    <details className="rounded-lg border bg-card p-4" open={open}>
      <summary className="cursor-pointer text-sm font-medium">More options: size, turn, color, details</summary>
      <div className="mt-4 space-y-5">
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Size</legend>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label htmlFor="cv-w" className="text-xs">Width (px)</Label><Input id="cv-w" inputMode="numeric" className="w-28" placeholder="keep" value={form.width} onChange={(e) => onChange({ width: e.target.value })} /></div>
            <div className="space-y-1"><Label htmlFor="cv-h" className="text-xs">Height (px)</Label><Input id="cv-h" inputMode="numeric" className="w-28" placeholder="keep" value={form.height} onChange={(e) => onChange({ height: e.target.value })} /></div>
            <div className="space-y-1">
              <Label className="text-xs">When both are set</Label>
              <Select value={form.fit} onValueChange={(v) => onChange({ fit: v as FitMode })}>
                <SelectTrigger className="w-40" aria-label="How the picture fits the size"><SelectValue /></SelectTrigger>
                <SelectContent>{FIT_MODES.map((m) => <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {sizeExtra}
          <p className="text-xs text-muted-foreground">Leave a box empty to keep that side. Set only one side and the other follows the picture's shape. {FIT_HELP[form.fit]}</p>
          <Check id="cv-noenlarge" checked={form.noEnlarge} onChange={(v) => onChange({ noEnlarge: v })} label="Never make it bigger than the original" />
        </fieldset>

        {showOptionalFill && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Background</legend>
            <Check id="cv-usebg" checked={form.useBg} onChange={(v) => onChange({ useBg: v })} label="Fill transparent areas and padding with a color" help="Off keeps the picture see-through where the format allows it." />
            {form.useBg && <ColorPicker id="bg-color-optional" form={form} onChange={onChange} />}
          </fieldset>
        )}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Turn and flip</legend>
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Rotate clockwise</Label>
              <Select value={form.rotate} onValueChange={(v) => onChange({ rotate: v })}>
                <SelectTrigger className="w-32" aria-label="Rotate"><SelectValue /></SelectTrigger>
                <SelectContent>{["0", "90", "180", "270"].map((d) => <SelectItem key={d} value={d}>{d === "0" ? "Not at all" : `${d} degrees`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Check id="cv-flop" checked={form.flop} onChange={(v) => onChange({ flop: v })} label="Mirror left to right" />
            <Check id="cv-flip" checked={form.flip} onChange={(v) => onChange({ flip: v })} label="Mirror top to bottom" />
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Color</legend>
          <Check id="cv-gray" checked={form.grayscale} onChange={(v) => onChange({ grayscale: v })} label="Black and white" help="Removes all color from the picture." />
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Details</legend>
          <Check id="cv-orient" checked={form.autoOrient} onChange={(v) => onChange({ autoOrient: v })} label="Turn photos upright automatically" help="Phone photos store a hidden rotation flag. This applies it so the picture looks right everywhere. Some browsers always apply it." />
          <Check id="cv-keep" checked={form.keepMeta} onChange={(v) => onChange({ keepMeta: v })} label="Keep camera, date and location details" help={metaHelp} />
          {!form.keepMeta && <p className="text-xs text-muted-foreground">Location and camera data are removed.</p>}
          {(form.format === "png" || form.format === "jpeg" || form.format === "tiff" || form.format === "bmp") && (
            <div className="space-y-1">
              <Label htmlFor="cv-dpi" className="text-xs">Print resolution (dpi)</Label>
              <Input id="cv-dpi" inputMode="numeric" className="w-28" placeholder="default" value={form.dpi} onChange={(e) => onChange({ dpi: e.target.value })} />
              <p className="text-xs text-muted-foreground">Print shops read this, for example 300. It does not change the pixel size.</p>
            </div>
          )}
        </fieldset>
      </div>
    </details>
  );
}
